import * as m from "../../model/model";

import { CCourse } from "./CCourse";
import { CCourses } from "./CCourses";
import { mapToList } from "../../utils/utils";

export type UserFormData = {
  key: string;
  admin: boolean;
  name: string;
  email: string;
  address: {
    line1: string;
    line2: string;
    postalCode: string;
    city: string;
    state: string;
    country: string;
  };
  currentCourse?: string;
  courses: Record<string, m.FbCourse>;
  tutorings: Record<string, m.FbTutoring>;
};

export interface UserAttendance {
  date: string;
  lesson: string;
}

export class CUser {
  json: m.User;

  constructor(json: m.User) {
    this.json = json;
  }

  get key() {
    return this.json.key;
  }

  get admin() {
    return this.json.admin;
  }
  set admin(value) {
    this.json.admin = value;
  }

  get name() {
    return this.json.name;
  }
  get email() {
    return this.json.email;
  }
  /* get created() {
    return new Date(this.json.created).toString();
  } */
  get address() {
    return this.json.address || {};
  }
  get progressCourse() {
    return this.coursesList[this.json.progress];
  }
  get courses() {
    return this.json.courses;
  }
  get purchases() {
    return this.json.purchases;
  }
  get orders() {
    return this.json.orders;
  }
  get coursesList(): m.FbCourse[] {
    return mapToList(this.json.courses);
  }
  get hasCourse() {
    return this.coursesList.length > 0;
  }
  fullCourses(courses: CCourses) {
    return this.coursesList.map((course) => courses?.getCourse(course.key));
  }
  unusedFullCourses(courses: CCourses): CCourse[] {
    const inuse = new Set(Object.keys(this.json.courses || {}));
    return courses?.courses.filter((course: CCourse) => !inuse.has(course.uid));
  }
  canAddCourse(courses: CCourses) {
    return this.unusedFullCourses(courses).length > 0;
  }
  get tutoringsList() {
    return mapToList(this.json.tutorings);
  }
  unusedTutorings(courses: CCourses) {
    const inuse = new Set(Object.keys(this.json.tutorings || {}));
    return courses.tutorings.filter(
      (tutoring: m.Tutoring) => !inuse.has(tutoring.uid)
    );
  }
  canAddTutoring(courses: CCourses) {
    return this.unusedTutorings(courses).length > 0;
  }

  get attendance(): UserAttendance[] {
    return Object.keys(this.json.attendance || {}).map((key) => ({
      date: key,
      lesson: this.json.attendance?.[key].lesson || "???",
    }));
  }

  getAttendanceForLesson(lesson: string) {
    return this.attendance.filter((a) => a.lesson.startsWith(lesson));
  }

  clone() {
    return new CUser({ ...this.json });
  }

  /**
   * Change one course to another course.
   * Not sure if this should be allowed?
   * Needed tho when a newly added course is changed.
   */
  changeCourse(course: CCourse, uid: string, courses: CCourses) {
    delete this.json.courses?.[course.key];
    const lessons = courses.getCourse(uid)?.lessons;
    const progress = lessons && lessons.length > 0 ? lessons[0].uid : "";
    if (!this.json.courses) this.json.courses = {};
    this.json.courses[uid] = {
      ...course.json,
      progress,
      material: {},
    };
  }

  changeCurrentCourse(uid: string) {
    this.json.currentCourse = uid;
  }

  addCourse(uid: string, courses: CCourses | undefined) {
    const start = new Date();
    const end = new Date(start.getTime() + 1000 * 60 * 60 * 24 * 365);
    const course = courses?.getCourse(uid);
    if (course) {
      const progress =
        course.lessons && course.lessons.length > 0
          ? course.lessons[0].uid
          : "";

      this.json = {
        ...this.json,
        courses: {
          ...(this.json.courses || {}),
          [course.uid]: {
            key: course.uid,
            startDate: start.toISOString(),
            endDate: end.toISOString(),
            progress,
            completedLessons: {},
            completedAudios: {},
            material: {},
          },
        },
        currentCourse: course.uid,
      };
    }
  }

  removeCourse(key: string) {
    // Create a new courses object without the specified key
    if (this.json.courses) {
      const newCourses = { ...this.json.courses };
      delete newCourses[key];

      // Update the entire courses object
      this.json = {
        ...this.json,
        courses: Object.keys(newCourses).length > 0 ? newCourses : undefined,
      };

      // Update current course if needed
      if (this.json.currentCourse === key) {
        if (Object.keys(newCourses).length > 0) {
          this.json.currentCourse = Object.keys(newCourses)[0];
        } else {
          this.json.currentCourse = undefined;
        }
      }
    }
  }

  completeLesson(lesson: string) {
    const json = { ...this.json };
    let progress = json.progress + 1;
    if (progress > json.course.lessons.length - 1) {
      progress = json.course.lessons.length - 1;
    }
    json.previousLessonUID = lesson;
    json.progressLessonUID = json.course.lessons[progress].uid;
    json.progress = progress;
    json.current = progress;
    this.json = json;
  }

  changeProgress(course: m.FbCourse, lesson: string) {
    const kurs = this.json.courses?.[course.key];
    // change completed lessons?
    if (this.json.courses && kurs) {
      this.json.courses[course.key] = {
        ...kurs,
        progress: lesson,
      };
    }
  }

  changeTutoring(tutoring: m.Tutoring, uid: string) {
    delete this.json.tutorings[tutoring.key];
    this.json.tutorings[uid] = {
      ...tutoring,
    };
  }

  addTutoring(courses: CCourses) {
    const start = new Date();
    const next = this.unusedTutorings(courses)?.[0];
    if (next) {
      if (!this.json.tutorings) this.json.tutorings = {};
      this.json.tutorings[next.uid] = {
        startDate: start.toISOString(),
        // TODO add sessions with timestamp, duration and notes
      };
    }
  }

  removeTutoring(key: string) {
    delete this.json.tutorings[key];
  }

  formData(): UserFormData {
    const data = {
      key: this.key || "",
      admin: this.admin || false,
      name: this.name || "",
      email: this.email || "",
      address: {
        line1: this.address.line1 || "",
        line2: this.address.line2 || "",
        postalCode: this.address.postalCode || "",
        city: this.address.city || "",
        state: this.address.state || "",
        country: this.address.country || "",
      },
      // currentCourse: this.currentCourse,
      courses: this.json.courses ? { ...this.json.courses } : {},
      /* courses: this.coursesList.reduce(
        (acc: { [key: string]: m.FbCourse }, course) => {
          acc[course.key] = {
            key: course.key,
            startDate: course.startDate,
            endDate: course.endDate,
            progress: course.progress,
            completedLessons: course.completedLessons,
          };
          return acc;
        },
        {}
      ), */
      // FIXME
      tutorings: this.json.tutorings ? { ...this.json.tutorings } : {},
      /* tutorings: this.tutoringsList.reduce(
        (acc: { [key: string]: m.FbTutoring }, tutoring) => {
          acc[tutoring.key] = {
            startDate: tutoring.startDate,
          };
          return acc;
        },
        {}
      ), */
    };

    // sanitize dates for form input
    Object.keys(data.courses).forEach((key) => {
      const course = data.courses[key];
      if (course) {
        data.courses[key] = {
          ...course,
          startDate: toDate(course.startDate),
          endDate: toDate(course.endDate),
        };
      }
    });
    Object.keys(data.tutorings).forEach((key) => {
      const tutoring = data.tutorings[key];
      if (tutoring) {
        data.tutorings[key] = {
          ...tutoring,
          startDate: toDate(tutoring.startDate),
        };
      }
    });

    return data;
  }

  /** @deprecated ? */
  parseFormData(
    formElements: Record<string, any>,
    courses: CCourses | undefined
  ) {
    console.log("parseFormData", formElements);
    const e = formElements;
    const data: m.FbUser = {
      // many of the json properties have already been set by
      // change handlers in the form
      ...this.json,
      name: e.name,
      address: {
        addressee: e.name,
        line1: e.line1,
        line2: e.line2,
        postalCode: e.postalCode,
        city: e.city,
        state: e.state,
        country: e.country,
      },
      currentCourse: e.currentCourse,
      // these will be reconstructed
      courses: {},
      tutorings: {},
    };

    this.coursesList.forEach((course) => {
      const progress = e[course.key + "-progress"];
      const fullCourse = courses?.getCourse(course.key);
      const index = fullCourse?.lessons.findIndex(
        (lesson: m.Lesson) => lesson.uid === progress
      );
      const completed = fullCourse?.lessons.filter(
        (lesson: m.Lesson, idx: number) => idx < (index || 0)
      );
      const completedLessons: Record<string, boolean> = {};
      (completed || []).forEach((lesson: m.Lesson) => {
        completedLessons[lesson.uid] = true;
      });

      /*
      // e.courses is now key value pairs
      if (!data.courses) data.courses = {};
       data.courses[course.key] = {
        startDate: e[course.key + "-startDate"].value,
        endDate: e[course.key + "-endDate"].value,
        progress,
        completedLessons, // TODO ?
      }; */
    });

    this.tutoringsList.forEach((tutoring) => {
      data.tutorings[tutoring.key] = {
        startDate: e[tutoring.key + "-startDate"],
      };
    });

    this.json = data as any;
  }
}

const toDate = (date: string) => {
  return date.split("T")[0];
};
